import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiClock, FiUser, FiArrowRight, FiSearch } from 'react-icons/fi';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { useTranslation } from '../hooks/useTranslation';
import blogData from '../data/blogData';
import './Blog.css';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const hydrated = useHydrated();
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'Panchangam', 'Vedic Astrology', 'Education', 'Remedies', 'Planetary Transit'];

  const filteredPosts = blogData.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogData[0];
  const regularPosts = filteredPosts.filter(post => post.id !== featuredPost.id);

  return (
    <div className="blog-page">
      <SEO
        title="Astrology & Panchangam Blog - Vedic Wisdom, Guides & Insights"
        description="Read expert articles on Vedic astrology, Panchangam, Navagraha, Nakshatra, Muhurtham, Rahu Kalam, Kundli reading, and Hindu calendar. Updated regularly by certified astrologers."
        keywords="astrology blog, vedic astrology articles, panchangam guide, nakshatra meaning, rahu kalam today, kundli reading, muhurtham dates, hindu calendar blog"
        canonicalPath="/blog"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "AstroEpanchang Astrology & Panchangam Blog",
            "description": "Explore insights, guides, and cosmic wisdom from expert Vedic astrologers"
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How often is the blog updated?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We publish new articles weekly covering current planetary transits, upcoming Hindu festivals, seasonal astrological guidance, and in-depth educational content on Vedic astrology topics. Subscribe to our newsletter to receive the latest articles directly in your inbox."
                }
              },
              {
                "@type": "Question",
                "name": "Are your astrological articles based on authentic Vedic texts?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, all our content is rooted in classical Vedic astrology texts including Brihat Parashara Hora Shastra (BPHS), Phaladeepika, Jataka Parijata, and Brihat Jataka. We also reference regional traditions like Tamil astrology (Jyothidam) and Kerala Jyotish. Our content bridges traditional wisdom with modern, accessible language."
                }
              },
              {
                "@type": "Question",
                "name": "Can I request a specific astrology topic to be covered?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely! We welcome topic suggestions from our readers. Use the Contact page to send us your request. Popular reader suggestions that we receive frequently are prioritized for upcoming articles. You can also ask questions in the comments section of any article."
                }
              }
            ]
          }
        ]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>{t('blog.title')} <span className="gradient-text">{t('blog.titleHighlight')}</span></h1>
          <p>{t('blog.subtitle')}</p>
        </motion.div>

        {/* Search and Filters */}
        <div className="blog-filters">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder={t('blog.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="category-filters">
            {categories.map(category => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === 'all' ? t('blog.allPosts') : category}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Post */}
        {selectedCategory === 'all' && searchTerm === '' && (
          <motion.div
            className="featured-post"
            initial={hydrated ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="featured-image">
              <img src={featuredPost.image} alt={featuredPost.title} />
              <span className="featured-badge">{t('blog.featured')}</span>
            </div>
            <div className="featured-content">
              <span className="post-category">{featuredPost.category}</span>
              <h2>{featuredPost.title}</h2>
              <p>{featuredPost.excerpt}</p>
              <div className="post-meta">
                <span><FiUser /> {featuredPost.author}</span>
                <span><FiClock /> {featuredPost.readTime}</span>
              </div>
              <Link to={`/blog/${featuredPost.slug}`} className="btn btn-primary">
                {t('blog.readArticle')} <FiArrowRight />
              </Link>
            </div>
          </motion.div>
        )}

        {/* Blog Grid */}
        <motion.div
          className="blog-grid"
          initial={hydrated ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {(searchTerm || selectedCategory !== 'all' ? filteredPosts : regularPosts).map((post, index) => (
            <motion.article
              key={post.id}
              className="blog-card"
              initial={hydrated ? { opacity: 0, y: 20 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={`/blog/${post.slug}`} className="blog-card-link">
                <div className="blog-card-image">
                  <img src={post.image} alt={post.title} />
                </div>
                <div className="blog-card-content">
                  <span className="post-category">{post.category}</span>
                  <h3>{post.title}</h3>
                  <p>{post.excerpt.substring(0, 120)}...</p>
                  <div className="post-meta">
                    <span><FiUser /> {post.author}</span>
                    <span><FiClock /> {post.readTime}</span>
                  </div>
                  <span className="read-more">{t('blog.readMore')}</span>
                </div>
              </Link>
            </motion.article>
          ))}
        </motion.div>

        {filteredPosts.length === 0 && (
          <div className="no-results">
            <p>{t('blog.noResults')}</p>
          </div>
        )}

        {/* Newsletter Section */}
        <div className="blog-newsletter">
          <h3>{t('blog.newsletter')}</h3>
          <p>{t('blog.newsletterDesc')}</p>
          <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
            <input type="email" placeholder={t('blog.emailPlaceholder')} />
            <button type="submit" className="btn btn-primary">{t('blog.subscribe')}</button>
          </form>
        </div>

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>Astrology & Panchangam Blog - Vedic Wisdom & Expert Guides</h2>
          <p>Explore our comprehensive collection of articles on Vedic astrology, Panchangam, Navagraha, Nakshatra, Muhurtham, and Hindu calendar traditions. Each article is written with authoritative content covering topics like Rahu Kalam effects, Kundli reading guides, Manglik Dosha remedies, Sade Sati periods, and Vastu Shastra principles.</p>
          <p>Whether you're a beginner learning about zodiac signs and ruling planets or an experienced astrology enthusiast seeking deeper insights into planetary transits and birth chart interpretation, our blog provides authentic Vedic knowledge to guide your spiritual and cosmic journey. Articles are regularly updated with the latest astronomical events and festival calendars.</p>

          <h3>Popular Astrology Topics We Cover</h3>
          <p><strong>Kundli & Birth Chart Guides:</strong> Learn how to read your Vedic birth chart, understand planetary placements in all 12 houses, interpret Dasha periods, and identify yogas (planetary combinations) that indicate wealth, career success, and spiritual progress in your Kundli.</p>
          <p><strong>Planetary Transits & Their Effects:</strong> Stay updated on Jupiter's annual transit (Guru Peyarchi), Saturn's transit (Shani Peyarchi/Sade Sati), and the Rahu-Ketu axis shift — and understand how these major transits affect each of the 12 Moon signs. Discover remedies and spiritual practices to maximize favorable transits and mitigate challenging ones.</p>
          <p><strong>Nakshatra Deep Dives:</strong> Each of the 27 Nakshatras (lunar mansions) has unique characteristics, presiding deities, ruling planets, and associated career paths and personality traits. Our Nakshatra series explores Ashwini, Bharani, Krittika, Rohini, and all 27 Nakshatras in detail — from personality analysis to marriage compatibility and health tendencies.</p>
          <p><strong>Festival & Muhurtham Guides:</strong> Understand the Vedic significance behind every Hindu festival — why Diwali is celebrated on Amavasya, the spiritual importance of Shivaratri's four prahar, and how Navratri's nine nights correspond to nine forms of the cosmic feminine. Also find practical guides for selecting auspicious muhurthams for weddings, business launches, and travel.</p>

          <h3>Remedies & Spiritual Practices</h3>
          <p>Our blog covers authentic Vedic remedies (Parihara) for various astrological challenges. Learn about Navagraha mantras and stotrams for each planet, Rudraksha bead recommendations based on your birth chart, gemstone therapy (Ratna Shastra) — including which gemstones are beneficial for your Lagna and Moon sign — and temple worship recommendations for planetary pacification. We also cover Yantra meditation, Homam (fire rituals), and charitable donations as karmic remedies prescribed in classical Vedic texts.</p>

          <h3>Frequently Asked Questions About Our Blog</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>How often is the blog updated?</h4>
              <p>We publish new articles weekly covering current planetary transits, upcoming Hindu festivals, seasonal astrological guidance, and in-depth educational content on Vedic astrology topics. Subscribe to our newsletter to receive the latest articles directly in your inbox.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Are your astrological articles based on authentic Vedic texts?</h4>
              <p>Yes, all our content is rooted in classical Vedic astrology texts including Brihat Parashara Hora Shastra (BPHS), Phaladeepika, Jataka Parijata, and Brihat Jataka. We also reference regional traditions like Tamil astrology (Jyothidam) and Kerala Jyotish. Our content bridges traditional wisdom with modern, accessible language.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Can I request a specific astrology topic to be covered?</h4>
              <p>Absolutely! We welcome topic suggestions from our readers. Use the Contact page to send us your request. Popular reader suggestions that we receive frequently are prioritized for upcoming articles. You can also ask questions in the comments section of any article.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
